import { isCompatStereotype, type OverlayEffect } from "@ae/domain";
import { Handle, type NodeProps, NodeResizer, Position, useReactFlow } from "@xyflow/react";
import type React from "react";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useDisplayMode } from "../../state/displayMode";
import { useKeymap } from "../../state/keymap";
import { claimObjectLock, releaseObjectLock } from "../../state/objectLocks";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";
import { colorForConcept } from "../layerColors";
import { getArchimateSvgContent, hasArchimateSvgIcon } from "./archimateIconLibrary";
import { getArchimateShape, shouldRenderArchimateShape } from "./archimateShapes";
import { getElementIcon, getStereotypeIcon } from "./elementIcons";
import { hernoemConcept } from "./hernoemConcept";

export interface ConceptNodeData {
  label: string;
  layer: string;
  typeLabel: string;
  typeId: string;
  conceptId: string;
  aspect: string;
  nodeStyle?: import("@ae/domain").NodeStyle;
  /**
   * Optional small "[Software System]" / "[Container]" subtitle shown
   * under the name. Set by Canvas.tsx when the active diagram is a C4
   * view-kind. Null on ArchiMate-only views.
   */
  stereotype?: string | null;
  /**
   * Optional concept-description shown under the stereotype on C4
   * views. Same gating as `stereotype` — Canvas.tsx populates only
   * when the active diagram is C4-flavoured.
   */
  description?: string | null;
  /**
   * Aantal direct-child viewState-entries die `hidden: true` hebben
   * t.o.v. deze instance als parent. Canvas.tsx vult dit per render
   * cycle. Wanneer >0 toont ConceptNode een kleine badge linksboven
   * (M3-carry-over §2.6 / M4-acceptance §7).
   */
  hiddenChildCount?: number;
  /**
   * True als deze instance één of meer direct genest kinderen heeft
   * (zichtbaar én verborgen tellen mee). Bepaalt of de chevron-
   * inklap-knop wordt getoond. False voor lege concepten / leaves.
   */
  hasChildren?: boolean;
  /**
   * True als alle directe kinderen `hidden: true` hebben — de
   * container-instance is dan ingeklapt. Stuurt de chevron-icon
   * (▸ collapsed / ▾ expanded). M4-acceptance §6 collapse-flow.
   */
  isCollapsed?: boolean;
  /**
   * True wanneer deze instance visuele children heeft via
   * `containerId`-nesting. ConceptNode forceert de naam dan
   * naar midden-boven (label-align-y=top) zodat 't kind-content
   * ruimte heeft i.p.v. dat het label de container-mid bedekt.
   */
  isContainer?: boolean;
  /** Box-grootte van de viewState-entry (px) — gebruikt voor
   *  compartments-render en interne afmetingen. */
  width?: number;
  height?: number;
  /** De tekengrootte waarbij de naam in dit vakje past — per object
   *  berekend door Canvas (`passendeFont`), niet meer als minimum over het
   *  hele diagram. Een expliciete keuze via de werkbalk wint hiervan. */
  diagramFontSize?: number;
  /**
   * Actieve stereotype-ids voor dit concept (M5+). Stuurt de
   * canvas-render-kleur via `colorForConcept` — bv. `c4.Microservice`
   * geeft het Microservice-token, terwijl `c4.Container` op
   * ApplicationComponent het app-code-Container-token geeft.
   */
  stereotypes?: readonly string[];
  /**
   * Concept-attributen — gebruikt voor render-discriminatoren zoals
   * legacy `c4_level` op ApplicationComponent. Niet alle attributen
   * worden geconsumeerd door de renderer; door 't object plat door te
   * geven blijft de helper toekomst-uitbreidbaar.
   */
  attributes?: Record<string, unknown>;
  /**
   * Pixel-offset van 't label t.o.v. z'n natural-positie (bepaald door
   * labelAlignX/Y + labelOutside). De gebruiker kan 't label vrij
   * verslepen — drag-handler in ConceptNode update setConceptLabelOffsetOnCanvas.
   * Per-instance opgeslagen in viewState.labelOffset.
   */
  labelOffset?: { x: number; y: number };
  /**
   * Status-bolletje linksboven op het concept. Door Canvas.tsx
   * berekend via `statusIndicatorFor` wanneer (a) de view-kind
   * `show_stereotype_status_indicators: true` zet, (b) een actief
   * stereotype een `status_indicator`-regel heeft en (c) het
   * gerefereerde attribuut een gemapte enum-waarde bevat. `null`
   * = geen bolletje rendered.
   */
  statusIndicator?: { color: string; label: string } | null;
  /**
   * Wanneer true: `colorForConcept` krijgt geen stereotypes mee, dus
   * het concept rendert in z'n basis-ArchiMate-laag-kleur (geel voor
   * Business, blauw voor Application, etc.) i.p.v. de C4/Sharp-
   * stereotype-kleur. Canvas.tsx zet dit op `archimate.*`-views zodat
   * een Application Component met `«c4.Container»` daar gewoon een
   * blauwe ArchiMate-component is, niet C4-blauw.
   */
  useArchimateBaseColors?: boolean;
  /**
   * BMC-views (`bmc.*`) renderen alle objecten als rechthoek, ongeacht
   * het ArchiMate-aspect van het base-element. Zo wordt een Value
   * (motivation → standaard octagon) of een BusinessProcess
   * (behavior → afgeronde hoeken) op een BMC-view een platte
   * rechthoek, consistent met de Osterwalder-conventie. Canvas.tsx
   * zet dit op alle bmc.*-views; `shapeStyle` retourneert dan altijd
   * de rectangle-variant en negeert het aspect.
   */
  forceRectangle?: boolean;
  /**
   * Sharp Detailed Concept compartment — populated by Canvas.tsx wanneer
   * deze concept-instance op een sharp.detailed-concept view child-
   * Attributes / Identifiers heeft via `containerId`-nesting. ConceptNode
   * rendert ze dan als compacte regels onder de naam i.p.v. als losse
   * boxen. Volgorde komt van Canvas (identifiers eerst, dan op sort_order).
   */
  compartmentChildren?: ReadonlyArray<{
    instanceId: string;
    conceptId: string;
    typeId: "sharp.Attribute" | "sharp.Identifier";
    label: string;
    isPrimary: boolean;
    sortOrder: number;
  }> | null;
  /**
   * Deterministisch overlay-effect (kleur/dim) uit de actieve regelset —
   * `null`/afwezig = geen overlay-match, render standaardkleur. Wint van
   * `nodeStyle` en de notatie-token-kleur (het is de actieve lens).
   */
  overlayEffect?: OverlayEffect | null;
  [key: string]: unknown;
}

/**
 * Check of een CSS-color-string opaak is. Conservatief: alleen
 * undefined / `transparent` / een rgba-of-#hexa met alpha 0 worden als
 * niet-opaak gerekend. Semi-transparant (alpha 0.5) telt nog als opaak
 * — dat dekt de lijken van descendants voldoende dat je ze niet meer
 * kunt onderscheiden, en we vermijden false-positives op kleuren die
 * de gebruiker met een lichte transparantie heeft ingekleurd.
 *
 * Gebruikt door de collapse-feature om te bepalen of de container's
 * natuurlijke achtergrond al voldoende dekkend is, of dat we een
 * canvas-witte fallback nodig hebben om descendants visueel te
 * verbergen.
 */
function isOpaqueColor(color: string | undefined): boolean {
  if (!color) return false;
  const c = color.trim().toLowerCase();
  if (c === "transparent" || c === "none") return false;
  // rgba/hsla met expliciete alpha 0
  const rgbaMatch = c.match(/^(?:rgba|hsla)\([^)]*[, /]\s*0(?:\.0+)?\s*\)$/);
  if (rgbaMatch) return false;
  // 8-of-4-digit hex met alpha 00
  if (/^#[0-9a-f]{6}00$/.test(c)) return false;
  if (/^#[0-9a-f]{3}0$/.test(c)) return false;
  return true;
}

function shapeStyle(
  aspect: string,
  selected: boolean,
  borderColor: string,
  bg: string,
  forceRectangle = false,
): {
  wrapper: CSSProperties;
  /**
   * When set, render an additional absolute-positioned background layer
   * (with clip-path / inset border) inside the wrapper. Used for the
   * motivation octagon so the wrapper itself stays a plain rectangle —
   * NodeResizer's handles + edge-drag zones live on the wrapper and would
   * otherwise be cut off by the wrapper's clip-path.
   */
  backgroundLayer?: CSSProperties;
  /**
   * Optionele tweede gestapelde achtergrond-laag bovenop `backgroundLayer`.
   * Gebruikt voor de motivation-octagon zodat de buitenste laag de
   * stroke-kleur is en de binnenste laag de fill-kleur — beide met hun
   * eigen clip-path, waardoor de "border" de schuine hoeken volgt
   * (i.p.v. een rechthoekige inset-box-shadow die de octagon-vorm niet
   * volgt).
   */
  foregroundLayer?: CSSProperties;
  useInsetBorder: boolean;
} {
  const selectedColor = "#2563eb";
  const stroke = selected ? selectedColor : borderColor;
  const strokeWidth = selected ? 3 : 2;
  // BMC-views (en evt. andere force-rectangle-contexten): platte
  // rechthoek, geen ArchiMate-aspect-vorm. Wint van alle aspect-takken
  // hieronder.
  if (forceRectangle) {
    return {
      wrapper: {
        borderRadius: 0,
        borderColor: stroke,
        borderWidth: strokeWidth,
        borderStyle: "solid",
      },
      useInsetBorder: false,
    };
  }
  switch (aspect) {
    case "behavior":
      return {
        wrapper: {
          borderRadius: 14,
          borderColor: stroke,
          borderWidth: strokeWidth,
          borderStyle: "solid",
        },
        useInsetBorder: false,
      };
    case "active_structure":
    case "passive_structure":
      return {
        wrapper: {
          borderRadius: 0,
          borderColor: stroke,
          borderWidth: strokeWidth,
          borderStyle: "solid",
        },
        useInsetBorder: false,
      };
    case "drawing_rect":
      return {
        wrapper: {
          borderRadius: 0,
          borderColor: stroke,
          borderWidth: strokeWidth,
          borderStyle: "solid",
        },
        useInsetBorder: false,
      };
    case "drawing_round":
      return {
        wrapper: {
          // Same look-and-feel als de behavior-shape (ronde hoeken)
          // maar met grotere radius voor visueel onderscheid t.o.v.
          // archimate-typed elementen.
          borderRadius: 18,
          borderColor: stroke,
          borderWidth: strokeWidth,
          borderStyle: "solid",
        },
        useInsetBorder: false,
      };
    case "junction": {
      // AndJunction = gevulde stip, OrJunction = open cirkel. Wrapper
      // krijgt 100%-radius zodat de box rond rendert; de inhoud (label)
      // wordt klein/verborgen omdat junctions visueel alleen 't symbool
      // zijn. Filled vs empty wordt door ConceptNode bepaald op
      // basis van typeId (en geeft de bg-kleur door aan deze helper —
      // hier rendert alleen de vorm).
      return {
        wrapper: {
          borderRadius: "50%",
          borderColor: stroke,
          borderWidth: strokeWidth,
          borderStyle: "solid",
        },
        useInsetBorder: false,
      };
    }
    case "motivation": {
      const c = 8;
      const sw = strokeWidth;
      // Outer octagon: 8 hoekpunten 8 px van iedere rand-hoek af. Wordt
      // ge-fillt met de stroke-kleur en vormt de zichtbare buitenrand.
      const outerClip = `polygon(${c}px 0, calc(100% - ${c}px) 0, 100% ${c}px, 100% calc(100% - ${c}px), calc(100% - ${c}px) 100%, ${c}px 100%, 0 calc(100% - ${c}px), 0 ${c}px)`;
      // Inner octagon: zelfde shape maar sw px naar binnen geschoven
      // langs elke rand. Diagonale hoeken sw px langer afgesneden zodat
      // de binnenrand óók de schuine vorm volgt; de zichtbare "border"
      // is daarmee uniform van dikte rond de hele octagon — geen
      // rechthoekige inset-box-shadow meer die de schuine hoeken
      // doorbreekt. Inset-correctie op de diagonalen (c + sw) is
      // een visuele benadering; voor de gangbare sw=2 wijkt 't <1px
      // af van de wiskundig-exacte normaal-offset (sw/√2 ≈ 1.4 px).
      const innerClip = `polygon(${c + sw}px ${sw}px, calc(100% - ${c + sw}px) ${sw}px, calc(100% - ${sw}px) ${c + sw}px, calc(100% - ${sw}px) calc(100% - ${c + sw}px), calc(100% - ${c + sw}px) calc(100% - ${sw}px), ${c + sw}px calc(100% - ${sw}px), ${sw}px calc(100% - ${c + sw}px), ${sw}px ${c + sw}px)`;
      return {
        // Wrapper is a transparent rectangle — NO clip-path here, otherwise
        // it would also clip NodeResizer's handles + edge-drag lines.
        wrapper: {
          borderRadius: 0,
          backgroundColor: "transparent",
        },
        // Buitenste laag: stroke-kleur, octagon-vorm. Vormt de zichtbare
        // donkere rand op alle 8 zijden (incl. diagonalen).
        backgroundLayer: {
          backgroundColor: selected ? selectedColor : stroke,
          clipPath: outerClip,
        },
        // Binnenste laag: fill-kleur, kleinere octagon-vorm. Bedekt het
        // binnenste van de buitenste laag waardoor alleen de
        // strokeWidth-brede rand zichtbaar blijft.
        foregroundLayer: {
          backgroundColor: bg,
          clipPath: innerClip,
          // Selectie-ring: extra inset-shadow op de binnenlaag zodat
          // de blauwe markering ook de schuine vorm volgt.
          ...(selected
            ? {
                boxShadow: "inset 0 0 0 4px rgba(59,130,246,0.35)",
              }
            : {}),
        },
        useInsetBorder: false,
      };
    }
    default:
      return {
        wrapper: {
          borderRadius: 6,
          borderColor: stroke,
          borderWidth: strokeWidth,
          borderStyle: "solid",
        },
        useInsetBorder: false,
      };
  }
}

export function ConceptNode({ id, data, selected, width, height }: NodeProps) {
  const d = data as ConceptNodeData;
  const ns = d.nodeStyle;
  const reactFlow = useReactFlow();
  const displayMode = useDisplayMode((s) => s.mode);
  // ArchiMate-weergavemodus (fase 3 van de v4-migratie). In v3.1-modus telt
  // het compat-stereotype mee voor kleur/icoon/vorm zodat een gemigreerd
  // concept er exact zo uitziet als vóór de type-merge; in v4-modus valt 't
  // weg en zie je het generieke Common-Domain-type.
  //
  // Let op de TEGENGESTELDE volgordeconventies: `colorForConcept` itereert
  // reverse (laatste = meest specifiek), `getStereotypeIcon` en
  // `getArchimateSvgContent` itereren voorwaarts (eerste = wint). Daarom
  // regelt de kleur-resolver z'n eigen ordening via `displayMode`, en bouwen
  // we hier apart een array voor notatie waarin de compat juist ACHTERAAN
  // staat — anders zou «Gap» het Risk-icoon overrulen.
  const allStereotypes = d.stereotypes ?? [];
  const compatStereotypes = allStereotypes.filter(isCompatStereotype);
  const ownStereotypes = allStereotypes.filter((s) => !isCompatStereotype(s));
  const compatForNotation = displayMode === "v31" ? compatStereotypes : [];
  const notationStereotypes = [...ownStereotypes, ...compatForNotation];
  // Stereotypes gaan normaal NIET naar de kleur-resolver op een
  // ArchiMate-base-view — conventie "ArchiMate-diagrammen tonen
  // ArchiMate-kleuren", zodat een C4- of Sharp-overlay daar niet z'n
  // eigen palet oplegt.
  //
  // Compat-stereotypes zijn daarvan UITGEZONDERD, en dat is geen detail:
  // ze zijn geen overlay maar dragen de ArchiMate-laagkleur zélf, nu die
  // informatie met de v4-merge uit het type verdwenen is. Onderdrukken we
  // ze hier, dan valt een gemigreerd bedrijfsproces terug op de
  // Common-Domain-grijstint en verliest de hele ArchiMate-view z'n
  // laagkleuren — precies wat er misging bij de eerste lokale flip
  // (2026-08-09). In v4-modus filtert `effectiveStereotypesForDisplay` de
  // compat er alsnog uit, dus daar blijft het grijs zoals bedoeld.
  const colorStereotypes = d.useArchimateBaseColors ? compatStereotypes : allStereotypes;
  const colors = colorForConcept({
    typeId: d.typeId,
    layer: d.layer,
    displayMode,
    ...(d.attributes ? { attributes: d.attributes } : {}),
    ...(colorStereotypes.length > 0 ? { stereotypes: colorStereotypes } : {}),
  });
  // Overlay-effect (conditional formatting) wint van zowel nodeStyle als de
  // notatie-token-kleur — het is de actieve lens. `null` → normale precedentie.
  const overlay = d.overlayEffect;
  // nodeStyle overrides take precedence over the layer-derived palette.
  const bg = overlay?.fill ?? ns?.fillColor ?? colors.bg;
  const textColor = overlay?.text ?? ns?.fontColor ?? colors.text;
  // strokeColor override applies to the node border AND to the
  // connect-handle dots, so both visually agree. When unset we fall
  // back to the layer-derived border colour. Speciaal geval:
  // strokeColor === "transparent" maakt de border onzichtbaar
  // (gebruiker kan zo een randloze rechthoek = vrij tekstblok
  // maken). Connect-handles blijven zichtbaar via de layer-derived
  // kleur, anders kun je geen relaties meer trekken vanaf zo'n node.
  const borderColor = overlay?.stroke ?? ns?.strokeColor ?? colors.border;
  const handleColor = borderColor === "transparent" ? colors.border : borderColor;
  // Stereotype-icoon heeft voorrang op type-icoon: een Assessment met
  // «Risk»-stereotype krijgt de Risk-decoratie, niet de Assessment-default.
  // Pas in 2026-05-17 toegevoegd voor de risk-overlay-refactor.
  const icon = getStereotypeIcon(notationStereotypes) ?? getElementIcon(d.typeId);
  const { wrapper, backgroundLayer, foregroundLayer, useInsetBorder } = shapeStyle(
    d.aspect,
    selected === true,
    borderColor,
    bg,
    d.forceRectangle === true,
  );
  // M6.5-W9 — ArchiMate-3.2-symbol-mode. Wanneer 't concept opt-in is
  // via `notation_style: "archimate"` (en geen stereotype dat de vorm
  // al claimt), pak de shape uit de library en override wrapper-vorm
  // (clipPath / borderRadius) — Decoration-overlay rendert later in
  // de wrapper.
  const archimateShape = shouldRenderArchimateShape(d.typeId, d.attributes, notationStereotypes)
    ? getArchimateShape(d.typeId)
    : null;
  // SVG-icoon-modus: wanneer 'n archimate-shape actief is én er een
  // SVG-icon bestaat voor dit type, gebruiken we de officiële SVG
  // i.p.v. de hand-getekende Decoration / clip-path. Wrapper wordt
  // transparant, corner-icon verbergen, geen clip-path. Geldt voor
  // élke shape met SVG — niet alleen `iconic`-flagged.
  const archimateSvg =
    archimateShape && hasArchimateSvgIcon(d.typeId, notationStereotypes)
      ? getArchimateSvgContent(d.typeId, notationStereotypes)
      : null;
  if (archimateShape && archimateSvg) {
    // SVG-modus overrules iconic + clipPath + Decoration. Maak de
    // wrapper neutraal zodat alleen de SVG zichtbaar is.
    wrapper.backgroundColor = "transparent";
    wrapper.borderColor = "transparent";
    wrapper.borderWidth = 0;
    wrapper.clipPath = undefined;
    wrapper.borderRadius = 0;
    wrapper.boxShadow = selected ? "0 0 0 2px rgba(59,130,246,0.5)" : "none";
  } else if (archimateShape) {
    if (archimateShape.clipPath) {
      wrapper.clipPath = archimateShape.clipPath;
      // Geen CSS-border voor clip-path-shapes — wordt afgekapt door clip.
      // Stroke wordt geschilderd via outlinePoints-SVG-overlay (zie hieronder).
      wrapper.borderColor = "transparent";
      wrapper.borderRadius = 0;
      wrapper.boxShadow = selected
        ? "0 0 0 3px rgba(59, 130, 246, 0.35), 0 1px 2px rgba(0,0,0,0.05)"
        : "0 1px 2px rgba(0,0,0,0.05)";
    }
    if (archimateShape.borderRadius !== undefined) {
      wrapper.borderRadius = archimateShape.borderRadius;
    }
    if (archimateShape.iconic) {
      // Iconic shapes: SVG IS de vorm, wrapper-rechthoek moet onzichtbaar
      // worden. Behoud de selectie-shadow als de node geselecteerd is
      // zodat NodeResizer-zone visueel terugkomt.
      wrapper.backgroundColor = "transparent";
      wrapper.borderColor = "transparent";
      wrapper.borderWidth = 0;
      wrapper.boxShadow = selected ? "0 0 0 2px rgba(59,130,246,0.5)" : "none";
    }
    if (archimateShape.dashedBorder) {
      wrapper.borderStyle = "dashed";
    }
    // ContentPadding: extra ruimte zodat het label binnen de zichtbare
    // shape valt en niet over de clip-path-rand heen loopt. Voor
    // arrow-shapes (right padding voor de pijlpunt), hexagon (links/
    // rechts voor de schuine zijden), Function (top/bottom voor V-dip),
    // etc. Wordt opgeteld bij de default px-3 py-2 wrapper-padding.
    if (archimateShape.contentPadding) {
      const cp = archimateShape.contentPadding;
      const base = archimateShape.iconic ? 0 : { top: 8, right: 12, bottom: 8, left: 12 };
      const b = typeof base === "object" ? base : { top: 0, right: 0, bottom: 0, left: 0 };
      wrapper.paddingTop = `${b.top + (cp.top ?? 0)}px`;
      wrapper.paddingRight = `${b.right + (cp.right ?? 0)}px`;
      wrapper.paddingBottom = `${b.bottom + (cp.bottom ?? 0)}px`;
      wrapper.paddingLeft = `${b.left + (cp.left ?? 0)}px`;
    }
  }
  // Junction-overrides: AND = filled circle (bg = stroke-color),
  // OR = empty circle (bg = white). Plus compacte vaste-grootte
  // omdat junctions visueel klein moeten zijn, niet de standaard
  // node-grootte. Wrapper-borderRadius staat al op 50% via shapeStyle.
  const isAndJunction = d.typeId === "archimate.AndJunction";
  const isOrJunction = d.typeId === "archimate.OrJunction";
  const isJunction = isAndJunction || isOrJunction;
  const junctionWrapper: CSSProperties = isJunction
    ? {
        width: 22,
        height: 22,
        minWidth: 22,
        minHeight: 22,
        backgroundColor: isAndJunction ? borderColor : "#ffffff",
        padding: 0,
      }
    : {};
  // C4-boundaries renderen per conventie met een streepjes-rand —
  // System / Container / Enterprise Boundary zijn visuele scope-
  // markeringen, geen vaste structuur. We zetten de default vóór
  // de user-override-stap zodat de toolbar nog kan winnen.
  const isC4Boundary =
    d.stereotypes?.includes("c4.SystemBoundary") ||
    d.stereotypes?.includes("c4.ContainerBoundary") ||
    d.stereotypes?.includes("c4.EnterpriseBoundary");
  if (isC4Boundary && !useInsetBorder) {
    wrapper.borderStyle = "dashed";
  }
  // User-override border pattern (solid / dashed / dotted) wins over
  // de aspect-default én de stereotype-default. Toegepast op de
  // wrapper voor sharp-rect shapes én op de inset-border voor de
  // motivation-octagon (die rendert via box-shadow, dus daar moeten
  // we niets — `dashed` op een octagon doet React Flow toch al niet).
  if (ns?.borderStyle && !useInsetBorder) {
    wrapper.borderStyle = ns.borderStyle;
    // "none" verbergt de hele border (CSS native). Voor drawing-rect
    // levert dat een randloze rechthoek = vrij tekstblok-effect.
    if (ns.borderStyle === "none") {
      wrapper.borderWidth = 0;
    }
  }
  // User-override border-radius (ellips-toggle in Size-groep). Wint van
  // archimateShape-default en aspect-default. Werkt voor sharp-rect-
  // shapes; voor octagons/iconic-svgs die zelf clipPath gebruiken (bv.
  // motivation-Requirement) heeft border-radius geen effect — daar
  // negeert CSS de waarde stilletjes, niet zo erg.
  if (ns?.borderRadiusOverride !== undefined) {
    wrapper.borderRadius = ns.borderRadiusOverride;
  }
  // Handles (de connection-bolletjes op de zijkanten) blijven altijd
  // pointer-active zodat de gebruiker vanaf de node-rand een relatie
  // kan trekken, maar zijn visueel onzichtbaar tot de node geselecteerd
  // is — dat houdt het canvas rustig wanneer er veel nodes staan.
  const handleStyle: React.CSSProperties = {
    background: handleColor,
    width: 8,
    height: 8,
    opacity: selected ? 1 : 0,
    transition: "opacity 0.1s",
  };
  // Hoek-aanhechtpunten liggen exact op de NodeResizer-hoek-handles.
  // Wanneer de node geselecteerd is (= resize-handles zichtbaar) maken we
  // ze pointer-inert, zodat de muis de resize-handle eronder raakt en je
  // het object vanuit de hoek kunt vergroten (universele conventie:
  // hoek = resize). Niet-geselecteerd blijven ze actief als extra
  // drag-to-connect-startpunten.
  const cornerHandleStyle: React.CSSProperties = {
    ...handleStyle,
    // Verbergen + inert wanneer geselecteerd, zodat enkel de blauwe
    // NodeResizer-hoek-handle zichtbaar is en reageert (geen connect-dot
    // die stiekem resize't). Niet-geselecteerd: actief connect-startpunt.
    opacity: selected ? 0 : handleStyle.opacity,
    pointerEvents: selected ? "none" : "auto",
  };
  const outerShadow = selected
    ? "0 0 0 3px rgba(59, 130, 246, 0.35), 0 1px 2px rgba(0,0,0,0.05)"
    : "0 1px 2px rgba(0,0,0,0.05)";
  // Label fontSize: een expliciete keuze via de werkbalk wint; anders de
  // maat waarbij de naam in dít vakje past. Eerder was dat het minimum over
  // álle vakjes op het diagram — bedoeld voor een gelijkmatig beeld, maar dan
  // trekt één smal vakje de tekst van het hele diagram omlaag.
  const labelStyle: React.CSSProperties = {
    fontWeight: ns?.bold ? 700 : 600,
    fontStyle: ns?.italic ? "italic" : "normal",
    textDecoration: ns?.underline ? "underline" : "none",
    fontSize: ns?.fontSize
      ? `${ns.fontSize}px`
      : d.diagramFontSize !== undefined
        ? `${d.diagramFontSize}px`
        : undefined,
    fontFamily: ns?.fontFamily ?? undefined,
  };

  // Label placement: a 3×3 anchor grid (BiZZdesign-style) plus an
  // inside/outside flag. The wrapper becomes a flex column so the label
  // can be aligned along both axes; for the "outside" variants we keep
  // the label in flow (so the wrapper still sizes to its content) and
  // displace it past the edge with a CSS transform.
  // Wanneer dit een visuele container is (heeft genest-children) ÉN
  // de gebruiker geen eigen label-alignment heeft gekozen (= label
  // staat momenteel in midden/centraal), forceer dan labelAlignY=top.
  // Een door de gebruiker bewust gezette andere uitlijning blijft
  // onaangetast.
  const userAx = ns?.labelAlignX ?? "center";
  const userAy = ns?.labelAlignY ?? "middle";
  const labelIsAtCenter = userAx === "center" && userAy === "middle";
  const ax = userAx;
  // Voor iconic-archimate-shapes (Actor/Collab/Interface/Interaction) zit
  // de SVG bovenin; label moet daaronder vallen. Push naar "bottom" wanneer
  // de gebruiker geen eigen uitlijning heeft gezet.
  const ay =
    archimateShape?.iconic && labelIsAtCenter
      ? "bottom"
      : d.isContainer && labelIsAtCenter
        ? "top"
        : userAy;
  const isCenter = ax === "center" && ay === "middle";
  const labelOutside = !!ns?.labelOutside && !isCenter;
  // Always apply the flex layout — also for the centre/centre case.
  // Otherwise the wrapper falls back to block layout and the label
  // collapses to the top of the box instead of being truly centred.
  const wrapperFlex: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: ay === "top" ? "flex-start" : ay === "bottom" ? "flex-end" : "center",
    alignItems: ax === "left" ? "flex-start" : ax === "right" ? "flex-end" : "center",
  };
  // Padding on the wrapper is px-3 py-2 (12 / 8 px). Outside-translation
  // pushes the label past the padding plus a 4 px gap, in whichever
  // direction(s) the X/Y anchors point.
  const dx = ax === "left" ? "calc(-100% - 16px)" : ax === "right" ? "calc(100% + 16px)" : "0";
  const dy = ay === "top" ? "calc(-100% - 12px)" : ay === "bottom" ? "calc(100% + 12px)" : "0";
  const labelTransform: string | undefined = labelOutside ? `translate(${dx}, ${dy})` : undefined;

  // Inline label editor — double-click the label to rename in the active
  // language. Persists via patchConcept on Enter or blur. Esc cancels.
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: geen hernoemen en geen grootte-handvatten.
  const readOnly = useSession((s) => s.readOnly);
  const [editing, setEditing] = useState(false);
  /** Houden wíj het slot op dit concept vast? Zodat we niet vrijgeven wat we
   *  nooit gekregen hebben. */
  const lockHeld = useRef(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const beginEdit = () => {
    if (useSession.getState().readOnly) return;
    // Voor drawing-types: start de inline-editor leeg in plaats van
    // met "(unnamed)" / "(zonder naam)" als de label-resolver een
    // placeholder heeft teruggegeven. Anders moet de gebruiker eerst
    // die placeholder weghalen voor hij iets eigens kan typen.
    const isDrawing = d.typeId.startsWith("drawing.");
    const looksUnnamed = d.label === "(unnamed)" || d.label === "(zonder naam)";
    setDraft(isDrawing && looksUnnamed ? "" : d.label);
    setEditing(true);

    // Het slot pakken we ernáást, niet ervóór: F2 hoort ogenblikkelijk te
    // zijn, en wachten op een netwerkaanroep zou de eerste aanslagen kosten
    // bij iedereen, altijd. Houdt een ander het vast, dan draaien we de
    // bewerkstand terug — dat kost hooguit een paar tekens, en alleen in het
    // zeldzame geval dat er werkelijk iemand anders in zit.
    // Zie `docs/architecture/gelijktijdigheid.md` §5.
    if (!d.conceptId) return;
    const target = d.conceptId;
    void claimObjectLock(target).then((denial) => {
      if (!denial) {
        lockHeld.current = true;
        return;
      }
      setEditing(false);
      useWorkspaceData.setState({
        lastError: denial.byBatch
          ? t("locks.heldByBatch")
          : t("locks.heldBy", { holder: denial.holder }),
      });
    });
  };

  /** Vrijgeven zodra het hernoemen klaar is — via Enter, Escape of blur. */
  const endEdit = () => {
    if (!lockHeld.current || !d.conceptId) return;
    lockHeld.current = false;
    releaseObjectLock(d.conceptId);
  };

  // F2-shortcut bridge: Editor.tsx dispatcht `ae:rename-concept` met de
  // conceptId van de selectie. Elke ConceptNode-instance van datzelfde
  // concept start z'n inline-editor — voor multi-instance is dat oké
  // omdat de rename via patchConcept toch concept-niveau is, het eerste
  // committed-blur wint.
  useEffect(() => {
    const onRename = (e: Event) => {
      const detail = (e as CustomEvent<{ conceptId: string }>).detail;
      if (!detail || detail.conceptId !== d.conceptId) return;
      beginEdit();
    };
    window.addEventListener("ae:rename-concept", onRename);
    return () => window.removeEventListener("ae:rename-concept", onRename);
    // beginEdit is een stabiele inline-closure die alleen `d` referenced;
    // d-changes worden via de outer-render gecaptured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.conceptId, d.label, d.typeId]);
  const persist = async () => {
    if (!editing) return;
    setEditing(false);
    endEdit();
    if (!workspaceId || !branchId || !d.conceptId) return;
    // De regels zelf staan in `hernoemConcept` — het banen-canvas hernoemt
    // langs dezelfde weg, en dat hoort hetzelfde te doen.
    await hernoemConcept({
      workspaceId,
      branchId,
      conceptId: d.conceptId,
      langKey,
      nieuweNaam: draft,
    });
  };

  // Collapse-opacity-guard: wanneer de natuurlijke wrapper-bg transparant
  // is (Grouping zonder fill, drawing.Rectangle zonder user-fill), valt
  // bij collapse de canvas-witte fallback in zodat de container z'n
  // descendants alsnog kan verbergen via z-stacking. Voor opake bg (BA
  // met layer-yellow, etc.) blijft de natuurlijke kleur gehandhaafd —
  // niets veranderen aan de visuele identiteit van de container.
  const naturalBgIsOpaque = isOpaqueColor(bg);
  const effectiveBg =
    d.isCollapsed === true && !backgroundLayer && !naturalBgIsOpaque ? "#ffffff" : bg;
  // M6.5-W11 focus-ring (slice B): dashed blauwe outline om de node
  // wanneer 'ie de canvas-focus heeft. Selector matcht ALLEEN deze node-
  // id (en alleen binnen canvas-scope) zodat focus-changes niet alle
  // ConceptNodes onnodig re-renderen.
  const isCanvasFocused = useKeymap((s) => s.scope === "canvas" && s.focusedCanvasId === id);
  return (
    <div
      data-concept-shape
      className={archimateShape?.iconic ? "transition-shadow" : "px-3 py-2 transition-shadow"}
      style={{
        minWidth: width !== undefined ? undefined : 140,
        width: width !== undefined ? "100%" : undefined,
        height: height !== undefined ? "100%" : undefined,
        // Background only when there's no separate clipped backgroundLayer;
        // for the motivation octagon the bg lives on that lower layer.
        backgroundColor: backgroundLayer ? undefined : effectiveBg,
        color: textColor,
        boxSizing: "border-box",
        position: "relative",
        // New stacking context — keeps the backgroundLayer's z-index:-1
        // contained to this node so it can't slip behind its siblings.
        isolation: backgroundLayer ? "isolate" : undefined,
        ...wrapper,
        // useInsetBorder = motivation-octagon. archimateShape.clipPath
        // schrijft z'n eigen boxShadow (inset + outer combined); niet
        // overschrijven met de generieke outerShadow.
        ...(useInsetBorder || archimateShape?.clipPath ? {} : { boxShadow: outerShadow }),
        ...wrapperFlex,
        ...junctionWrapper,
        // Overlay-dim: gedimd renderen (context behouden zonder ruis). Na alle
        // shape-spreads zodat 't universeel geldt, ongeacht render-modus.
        ...(overlay?.dim ? { opacity: 0.35 } : {}),
      }}
    >
      {backgroundLayer && !archimateShape?.iconic && !archimateSvg && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ ...backgroundLayer, zIndex: -1 }}
        />
      )}
      {foregroundLayer && !archimateShape?.iconic && !archimateSvg && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ ...foregroundLayer, zIndex: -1 }}
        />
      )}
      {archimateSvg && (
        // ArchiMate-officieel SVG-icoon. `stroke="currentColor"` in de
        // SVG-bronfile volgt de wrapper-color (= borderColor) zodat de
        // tekening de juiste kleur krijgt. Tailwind arbitrary-variant
        // `[&_svg]:h-full [&_svg]:w-full` zorgt dat de SVG de wrapper
        // vult (overschrijft de ingebakken width/height-attributes).
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [&_svg]:h-full [&_svg]:w-full"
          style={{ color: borderColor }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: archimateSvg }}
        />
      )}
      {!archimateSvg &&
        (() => {
          // Capitalized local-binding zodat JSX `<Decoration />` netjes
          // als component parseert (een lowercase-base member-expression
          // werkt in praktijk, maar dit voorkomt typing-edge-cases).
          const Decoration = archimateShape?.Decoration;
          if (!Decoration) return null;
          return <Decoration fill={bg} stroke={borderColor} />;
        })()}
      {!archimateSvg && archimateShape?.outlinePoints && (
        // Stroke-outline-overlay voor clip-path-shapes — schildert de
        // border langs de polygon-edges (CSS box-shadow inset wordt door
        // clip-path afgekapt en geeft geen rand op schuine kanten).
        // strokeLinejoin="round" voorkomt miter-spikes op acute hoeken
        // (parallelogram, chevron-arrow, etc.).
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon
            points={archimateShape.outlinePoints}
            fill="none"
            stroke={selected ? "#2563eb" : borderColor}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            style={{ strokeWidth: selected ? 3 : 2 }}
          />
        </svg>
      )}
      {isCanvasFocused && (
        <div
          aria-hidden
          // Dashed zwart focus-ring 4 px buiten de node-rand. Zwart in
          // plaats van blauw zodat 'ie zichtbaar blijft op de blauwe
          // C4-kleuren; 3 px breedte zodat 'ie ook bij kleine nodes goed
          // afsteekt.
          className="pointer-events-none absolute"
          style={{
            inset: -4,
            border: "3px dashed #000",
            borderRadius: 6,
            zIndex: 20,
          }}
        />
      )}
      <NodeResizer
        isVisible={selected === true && !readOnly}
        minWidth={24}
        minHeight={24}
        color="#2563eb"
        // Bij containers met kinderen beperken we resize tot
        // rechts/onder/bottom-right om de React Flow-quirk te omzeilen
        // waarbij top/left-resize de parent-positie verschuift maar de
        // relative-positie van de kinderen niet meecompenseert — het
        // gevolg was dat kinderen kort buiten de container's frame
        // vlogen en niet altijd netjes terugkeerden. Voor losse nodes
        // (geen kinderen) is dit geen probleem; daar blijven alle 8
        // handles beschikbaar. Bug B in M4-acceptance.md.
        {...(d.hasChildren
          ? { allowedResizeHandles: ["right", "bottom", "bottom-right"] as const }
          : {})}
      />

      {icon && !archimateShape && !ns?.hideIcon && (
        // Type-icoon staat default rechts-boven. Alleen wanneer 't label
        // EXACT op rechts-BOVEN is uitgelijnd, schuift 't icoon naar
        // links-boven — anders zou 't icoon de tekst-rechter-rand
        // wegduwen. Bij rechts-midden of rechts-onder zit 't label niet
        // in de top-rechter hoek; daar mag 't icoon blijven.
        <div
          className={`pointer-events-none absolute top-1 ${
            ax === "right" && ay === "top" ? "left-1" : "right-1"
          } h-4 w-4 opacity-80`}
          aria-hidden
        >
          {icon}
        </div>
      )}

      {d.statusIndicator && (
        <div
          className="pointer-events-none absolute top-1 left-1 h-3 w-3 rounded-full border border-white/60 shadow-sm"
          style={{ backgroundColor: d.statusIndicator.color }}
          aria-label={d.statusIndicator.label}
          role="img"
        />
      )}

      {(d.hiddenChildCount ?? 0) > 0 && (
        <div
          className={`pointer-events-auto absolute top-1 ${
            d.statusIndicator ? "left-6" : "left-1"
          } flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-700 px-1 font-semibold text-[10px] text-white leading-none shadow-sm`}
          title={t("canvas.hiddenBadge.tooltip", { count: d.hiddenChildCount ?? 0 })}
        >
          {d.hiddenChildCount}
        </div>
      )}

      {d.hasChildren && (
        <button
          type="button"
          // `nodrag` voorkomt dat React Flow de container drag start
          // bij click op deze knop. Aparte klikgebied bottom-left of
          // bottom-right (zie hieronder) zodat 'ie niet overlapt met
          // de hidden-badge (top-left) of de type-icon (top-right).
          // React Flow's `id`-prop is de instance-id (viewState-key)
          // die toggleCollapseContainer verwacht. Session-check
          // (workspaceId + branchId) zit binnen de click-handler
          // i.p.v. de render-condition: als de session-store nog niet
          // gehydrateerd is op het moment dat React Flow ConceptNode
          // mount, mag de knop nog steeds visueel aanwezig zijn
          // (toggleCollapseContainer no-opt zelf).
          //
          // Positie: standaard bottom-left. Maar wanneer het label
          // van deze container linksonder uitlijnt (ax=left + ay=bottom)
          // overlapt de chevron met de label-tekst — dan plaatsen we
          // 'm bottom-right zodat beide leesbaar blijven. Zie
          // M4-acceptance.md "Feature E — chevron-positionering".
          className={`nodrag pointer-events-auto absolute bottom-1 ${
            ax === "left" && ay === "bottom" ? "right-1" : "left-1"
          } flex h-3 w-3 items-center justify-center rounded border border-slate-300 bg-white/95 font-semibold text-[10px] text-slate-700 leading-none shadow-sm hover:border-slate-400 hover:bg-slate-50`}
          onClick={(e) => {
            e.stopPropagation();
            if (!workspaceId || !branchId) return;
            useWorkspaceData.getState().toggleCollapseContainer(workspaceId, branchId, id);
          }}
          title={
            d.isCollapsed ? t("canvas.collapseButton.expand") : t("canvas.collapseButton.collapse")
          }
          aria-label={
            d.isCollapsed ? t("canvas.collapseButton.expand") : t("canvas.collapseButton.collapse")
          }
        >
          <span aria-hidden>{d.isCollapsed ? "▸" : "▾"}</span>
        </button>
      )}

      {editing ? (
        <input
          ref={inputRef}
          // `nodrag` keeps React Flow from starting a node-drag on the
          // input's mousedown; without it, typing inside the box would
          // also yank the whole concept around.
          className="nodrag w-full border-current border-b bg-transparent pr-5 font-semibold text-sm leading-tight outline-none"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            void persist();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
              endEdit();
            }
            // Stop the canvas-level Delete/Backspace handler from
            // deleting the node while the user is editing the label.
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : (
        // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow node; selectie via React-Flow, geen per-element tab-stop
        <div
          // Label-padding alleen bij conflict-gevallen met 't icoon:
          //  - rechts-boven uitlijnen: icoon staat dan links → pl-5
          //  - center of links uitlijnen: icoon staat rechts en label
          //    kan met lange tekst de rechter-zone raken → pr-5
          //  - rechts-midden en rechts-onder: label staat op een
          //    andere rij dan 't icoon, geen overlap mogelijk → geen
          //    padding (label kan zo écht tot de rechter rand reiken)
          //  - geen icoon (hideIcon / archimateShape) → idem geen padding.
          className={`cursor-text leading-tight ${
            !icon || ns?.hideIcon || archimateShape
              ? ""
              : ax === "right" && ay === "top"
                ? "pl-5"
                : ax !== "right"
                  ? "pr-5"
                  : ""
          }`}
          style={{
            fontSize: "0.875rem",
            ...labelStyle,
            // labelTextAlign (expliciet door user gezet via toolbar) wint
            // van de fallback-afleiding uit labelAlignX. Backward-compat
            // op entries zonder labelTextAlign: het label volgt het anker
            // zoals voorheen.
            textAlign:
              ns?.labelTextAlign ?? (ax === "left" ? "left" : ax === "right" ? "right" : "center"),
            // Combineer labelOutside-transform (verschuiving buiten de
            // wrapper) met de user-set labelOffset (free-drag), zodat
            // sleep-positie bovenop de outside-uitlijning komt.
            transform:
              labelTransform || d.labelOffset
                ? `${labelTransform ?? ""} translate(${d.labelOffset?.x ?? 0}px, ${d.labelOffset?.y ?? 0}px)`.trim()
                : undefined,
            ...(labelTransform ? { whiteSpace: "nowrap" } : {}),
            // Word-wrap-strategie voor lange labels die niet passen.
            // hyphenate = browser-hyphenation MET fallback op mid-word
            // break voor woorden zonder bekend hyphenation-patroon
            // (eigennamen, acroniemen, korte/onbekende woorden). De
            // combinatie hyphens:auto + overflow-wrap:anywhere laat de
            // browser EERST hyphenation-punten proberen; pas als die
            // niet volstaan, breekt 'ie midden in 't woord — best of
            // both worlds. break-anywhere modus slaat hyphenation
            // helemaal over en breekt altijd mid-word zonder streepje.
            ...(ns?.labelWrap === "hyphenate"
              ? { hyphens: "auto", overflowWrap: "anywhere" }
              : ns?.labelWrap === "break-anywhere"
                ? { overflowWrap: "anywhere" }
                : {}),
            // Een kwartslag gedraaid, van onder naar boven — de leesrichting
            // die je in een smal, hoog vakje verwacht.
            ...(ns?.labelOrientation === "vertical"
              ? { writingMode: "vertical-rl" as const, transform: "rotate(180deg)" }
              : {}),
          }}
          // `lang` is alleen relevant voor hyphenate-modus — browser
          // gebruikt 't om de juiste hyphenation-patronen te kiezen.
          {...(ns?.labelWrap === "hyphenate" ? { lang: i18n.language } : {})}
          onMouseDown={(e) => {
            // Drag-to-reposition. Stop propagation zodat React Flow
            // de node niet ook gaat slepen. screenToFlowPosition voor
            // zoom-correcte pixel-deltas.
            if (!workspaceId || !branchId || !d.conceptId) return;
            e.stopPropagation();
            const startScreen = { x: e.clientX, y: e.clientY };
            const startFlow = reactFlow.screenToFlowPosition(startScreen);
            const baseOffset = d.labelOffset ?? { x: 0, y: 0 };
            let moved = false;
            const onMove = (ev: MouseEvent) => {
              const f = reactFlow.screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
              const dx = f.x - startFlow.x;
              const dy = f.y - startFlow.y;
              // Threshold om micro-bewegingen niet als drag te tellen
              // (zodat single-click voor selectie blijft werken).
              if (!moved && Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
              moved = true;
              useWorkspaceData.getState().setConceptLabelOffsetOnCanvas(workspaceId, branchId, id, {
                x: baseOffset.x + dx,
                y: baseOffset.y + dy,
              });
            };
            const onUp = () => {
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            // Modifier-doubleClick reset labelOffset (free-drag-positie);
            // gewone doubleClick start inline-edit van de naam.
            if (e.metaKey || e.ctrlKey || e.altKey) {
              if (workspaceId && branchId && d.conceptId) {
                useWorkspaceData
                  .getState()
                  .setConceptLabelOffsetOnCanvas(workspaceId, branchId, id, null);
              }
              return;
            }
            beginEdit();
          }}
          title={t("canvas.node.nameTitle")}
        >
          {/*
            ArchiMate 4-weergave: guillemet-badge bóven de naam met de
            oorspronkelijke 3.1-identiteit. Zonder deze badge zien vier
            soorten processen er identiek uit zodra ze op het generieke
            `archimate.Process` zitten; C260 §14.2 stelt de guillemet juist
            als standaardnotatie voor specialisaties. Alleen in v4-modus —
            in v3.1 draagt het TYPE-label die informatie al, en zou de badge
            dubbelop zijn. Concepten zonder compat-stereotype (born-v4 of
            nooit-gemergede types) krijgen niets.
          */}
          {displayMode === "v4" &&
            (() => {
              const compat = (d.stereotypes ?? []).find(isCompatStereotype);
              if (!compat) return null;
              const badge = getRegistry().getStereotype(compat);
              if (!badge) return null;
              return (
                <div
                  className="font-normal opacity-70"
                  style={{
                    fontSize: `${Math.max(8, Math.round((ns?.fontSize ?? 14) * 0.69))}px`,
                    marginBottom: 1,
                    lineHeight: 1.1,
                  }}
                >
                  {resolveBilingual(badge.label, i18n.language)}
                </div>
              );
            })()}
          {/*
            Drawing-primitieven (drawing.Rectangle / drawing.RoundedRectangle)
            mogen leeg blijven — een vrije rechthoek is een visuele
            annotatie, geen entity. Geen placeholder, geen "(unnamed)",
            geen "(zonder naam)" — gewoon niets. `resolveBilingual` kan
            "(unnamed)" teruggeven wanneer het name-object volledig leeg
            is (`{}`); dat filteren we hier ook weg voor drawing-types.
            Voor archimate/c4 concepten tonen we de "(zonder naam)"
            placeholder wel, omdat die altijd een betekenisvolle naam
            horen te hebben.
          */}
          {(() => {
            const isDrawing = d.typeId.startsWith("drawing.");
            const looksUnnamed = !d.label || d.label === "(unnamed)" || d.label === "(zonder naam)";
            // Junctions zijn symbolen (filled/empty cirkel), zonder
            // visueel tekst-label — naam-veld wel persisteren in de DB
            // maar render verbergen.
            if (d.typeId === "archimate.AndJunction" || d.typeId === "archimate.OrJunction") {
              return "";
            }
            // «Concept Constraint»: de regel-tekst zelf is de
            // betekenisvolle label — de generieke "Concept Constraint"-
            // naam zou alleen ruis zijn boven de regel-tekst. Dus naam
            // weglaten zodra dat stereotype actief is en er een
            // description (= rule_statement, gevuld door Canvas.tsx)
            // beschikbaar is.
            if (d.stereotypes?.includes("sharp.ConceptConstraint") && d.description) {
              return "";
            }
            if (isDrawing) return looksUnnamed ? "" : d.label;
            return d.label || t("canvas.unnamed");
          })()}
          {/*
            Stereotype + description scale with the name. Default name
            font-size is 0.875rem (14px). When the user picks an override
            via the toolbar (`ns.fontSize`, in px), we apply the same
            ratios — stereotype ≈ 69 % of the name, description ≈ 80 %.
            That keeps the C4 hierarchy visually consistent at any size.
          */}
          {(() => {
            const baseNamePx = ns?.fontSize ?? 14; // 0.875rem default
            const stereotypePx = Math.max(8, Math.round(baseNamePx * 0.69));
            const descriptionPx = Math.max(9, Math.round(baseNamePx * 0.8));
            return (
              <>
                {/*
                  C4 stereotype subtitle — "[Software System]" / "[Container]"
                  etc. Only set on C4-flavoured views (Canvas.tsx decides),
                  so a no-op on ArchiMate diagrams.
                */}
                {d.stereotype && (
                  <div
                    className="font-normal opacity-70"
                    style={{ fontSize: `${stereotypePx}px`, marginTop: 1, lineHeight: 1.1 }}
                  >
                    {d.stereotype}
                  </div>
                )}
                {/*
                  Concept description under the stereotype — C4 convention
                  is name (large) → stereotype (small) → description
                  (medium). Word-wrap is on so longer descriptions flow
                  within the box instead of overflowing horizontally.
                */}
                {d.description && (
                  <div
                    className="font-normal opacity-85"
                    style={{
                      fontSize: `${descriptionPx}px`,
                      marginTop: 4,
                      lineHeight: 1.25,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {d.description}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Sharp Detailed Concept compartment — compacte lijst van child-
          Attributes / Identifiers, gestapeld onder de naam. Identifiers
          krijgen een onderstreping + #-prefix; gewone Attributes alleen
          de naam. Drag-up/down per regel update sort_order zodat de
          gebruiker de volgorde direct kan herschikken. */}
      {d.compartmentChildren && d.compartmentChildren.length > 0 && !d.isCollapsed && (
        <SharpCompartment
          instanceId={id}
          rows={d.compartmentChildren}
          // Volgt de toolbar-tekstuitlijning (NodeStyle.labelTextAlign)
          // letterlijk, net als het label zelf. De ⇤/⇔/⇥-knoppen zetten
          // 'm op left/center/right; default = center (de center-knop slaat
          // op als null, dus null == center — anders zouden left en center
          // er identiek uitzien). Links uitlijnen = ⇤ klikken.
          align={ns?.labelTextAlign ?? "center"}
        />
      )}

      <Handle id="left" type="source" position={Position.Left} style={handleStyle} />
      <Handle id="right" type="source" position={Position.Right} style={handleStyle} />
      <Handle id="top" type="source" position={Position.Top} style={handleStyle} />
      <Handle id="bottom" type="source" position={Position.Bottom} style={handleStyle} />
      {/* Hoek-aanhechtpunten (2026-05-26): vier extra handles op de
          hoeken van de node-rand. Drag-to-connect werkt identiek aan de
          mid-edge handles; routing-/angle-logica blijft ongewijzigd. We
          binden ze aan een base-Position (Top/Bottom) zodat React Flow
          ze tot het juiste handle-element rekent, en overschrijven dan
          left/right + transform zodat ze visueel exact op de hoek
          staan. handle-id's (`top-left`, `top-right`, etc.) worden in
          de viewState bewaard als `sourceHandle`/`targetHandle` mocht
          de gebruiker daar later edge-routing op willen baseren — voor
          nu zijn 't puur extra drag-startpunten. */}
      <Handle
        id="top-left"
        type="source"
        position={Position.Top}
        style={{ ...cornerHandleStyle, left: 0, transform: "translate(-50%, -50%)" }}
      />
      <Handle
        id="top-right"
        type="source"
        position={Position.Top}
        style={{ ...cornerHandleStyle, left: "100%", transform: "translate(-50%, -50%)" }}
      />
      <Handle
        id="bottom-left"
        type="source"
        position={Position.Bottom}
        style={{ ...cornerHandleStyle, left: 0, transform: "translate(-50%, 50%)" }}
      />
      <Handle
        id="bottom-right"
        type="source"
        position={Position.Bottom}
        style={{ ...cornerHandleStyle, left: "100%", transform: "translate(-50%, 50%)" }}
      />
    </div>
  );
}

/**
 * Compacte compartment-render voor Sharp Detailed Concept.
 *
 * Toont een gestapelde lijst van child Attributes / Identifiers binnen
 * de parent-Concept-box. Identifiers staan eerst (met onderstreping +
 * #-prefix), Attributes volgen. Drag-up/down per regel verplaatst 'm
 * binnen de groep door sort_order te swappen met de buurman. Single-
 * click selecteert het child-concept zodat de gebruiker via 't
 * AttributePanel datatype + andere properties kan wijzigen.
 */
function SharpCompartment({
  instanceId,
  rows,
  align = "left",
}: {
  instanceId: string;
  rows: ReadonlyArray<NonNullable<ConceptNodeData["compartmentChildren"]>[number]>;
  align?: "left" | "center" | "right";
}) {
  void instanceId;
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  // Rechtsklik-menu op een compartiment-regel (attribuut/identifier).
  const [rowMenu, setRowMenu] = useState<{
    conceptId: string;
    label: string;
    kind: string;
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    if (!rowMenu) return;
    const close = () => setRowMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [rowMenu]);

  const openRow = (conceptId: string) => {
    useUi.getState().selectConcept(conceptId);
    useUi.getState().setAttributePanelOpen(true);
  };
  const deleteRow = (conceptId: string, label: string, kind: string) => {
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    void (async () => {
      const ok = await confirmHardDelete({
        workspaceId,
        branchId,
        conceptIds: [conceptId],
        lang: "nl",
        standaloneEdgeCount: 0,
        conceptName: () => ({ nl: label, en: label }),
        kindLabel: kind,
      });
      if (!ok) return;
      await ws.removeBatch(workspaceId, branchId, [conceptId], []);
    })();
  };

  const beginRowDrag = (childIndex: number) => (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (!workspaceId || !branchId) return;
    e.preventDefault();
    e.stopPropagation();
    // Splits in twee groepen — identifiers en attributes — drag werkt
    // ALLEEN binnen dezelfde groep (verwisseling tussen identifier en
    // attribute is semantisch onlogisch, dat zou een type-wijziging
    // vragen).
    const sourceChild = rows[childIndex];
    if (!sourceChild) return;
    const groupSiblings = rows
      .map((c, i) => ({ child: c, idx: i }))
      .filter(({ child }) => child.typeId === sourceChild.typeId);
    if (groupSiblings.length <= 1) return;
    const startY = e.clientY;
    let lastSwapTarget = childIndex;
    const ROW_HEIGHT = 22;
    const onMove = (ev: MouseEvent) => {
      const dy = ev.clientY - startY;
      const stepCount = Math.round(dy / ROW_HEIGHT);
      const groupPos = groupSiblings.findIndex((s) => s.idx === childIndex);
      const targetGroupPos = Math.max(0, Math.min(groupSiblings.length - 1, groupPos + stepCount));
      const targetEntry = groupSiblings[targetGroupPos];
      if (!targetEntry) return;
      if (targetEntry.idx !== lastSwapTarget) {
        lastSwapTarget = targetEntry.idx;
        // Bepaal nieuwe sort_orders door de groep in z'n geheel opnieuw
        // te nummeren; we plaatsen sourceChild op targetGroupPos.
        const reordered = groupSiblings.filter((s) => s.idx !== childIndex).map((s) => s.child);
        reordered.splice(targetGroupPos, 0, sourceChild);
        const ws = useWorkspaceData.getState();
        for (let i = 0; i < reordered.length; i++) {
          const c = reordered[i];
          if (!c) continue;
          const newOrder = (i + 1) * 10;
          const concept = ws.store.getConcept(c.conceptId);
          if (!concept) continue;
          if (concept.attributes.sort_order === newOrder) continue;
          void ws.patchConcept(workspaceId, branchId, c.conceptId, {
            attributes: { ...concept.attributes, sort_order: newOrder },
          });
        }
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      style={{
        // alignSelf stretch zodat de compartment de volle node-breedte
        // pakt — anders centreert de node-content-wrapper (alignItems)
        // 't blok en lijkt 't gecentreerd ondanks textAlign.
        alignSelf: "stretch",
        width: "100%",
        marginTop: 6,
        padding: "4px 6px",
        borderTop: "1px dashed rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        fontSize: 12,
        lineHeight: 1.4,
        textAlign: align,
      }}
    >
      {rows.map((c, idx) => {
        const isIdentifier = c.typeId === "sharp.Identifier";
        return (
          // biome-ignore lint/a11y/noStaticElementInteractions: React-Flow node; selectie via React-Flow, geen per-element tab-stop
          <div
            key={c.instanceId}
            className="nodrag"
            onMouseDown={beginRowDrag(idx)}
            onClick={(e) => {
              e.stopPropagation();
              useUi.getState().selectConcept(c.conceptId);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openRow(c.conceptId);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              useUi.getState().selectConcept(c.conceptId);
              setRowMenu({
                conceptId: c.conceptId,
                label: c.label,
                kind: isIdentifier ? "Identifier" : t("canvas.sharp.attribute"),
                x: e.clientX,
                y: e.clientY,
              });
            }}
            style={{
              cursor: "grab",
              userSelect: "none",
              padding: "1px 4px",
              borderRadius: 2,
              textAlign: align,
              textDecoration: isIdentifier ? "underline" : "none",
              fontWeight: c.isPrimary ? 700 : 400,
              backgroundColor:
                c.conceptId === selectedConceptId ? "rgba(59,130,246,0.18)" : undefined,
            }}
            title={
              isIdentifier ? t("canvas.sharp.identifierTitle") : t("canvas.sharp.attributeTitle")
            }
          >
            {isIdentifier ? `# ${c.label}` : c.label}
          </div>
        );
      })}
      {rowMenu &&
        createPortal(
          <div
            className="fixed z-50 min-w-[150px] rounded border border-slate-300 bg-white py-1 text-sm shadow-lg"
            style={{ left: rowMenu.x, top: rowMenu.y }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="block w-full px-3 py-1 text-left text-slate-700 hover:bg-slate-100"
              onClick={() => {
                openRow(rowMenu.conceptId);
                setRowMenu(null);
              }}
            >
              {t("canvas.contextMenu.edit")}
            </button>
            <button
              type="button"
              className="block w-full px-3 py-1 text-left text-rose-700 hover:bg-rose-50"
              onClick={() => {
                const { conceptId, label, kind } = rowMenu;
                setRowMenu(null);
                deleteRow(conceptId, label, kind);
              }}
            >
              {t("canvas.contextMenu.delete")}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
